import { nonNullable } from 'lib/array/arrayUtils'
import { getPlural } from 'lib/string/pluralize'
import { countOccupants, formatOccupantsShort } from 'lib/offer/occupancyUtils'
import { onlyDefinedProperties } from 'lib/object/objectUtils'
import { getOccupancyFromSearchStrings } from 'lib/search/searchUtils'
import { sanitiseChildrenAges } from 'lib/search/occupancy'
import {
  toSearchStringAgeThresholds,
  type AssistantOccupancyAgeThresholds,
} from './assistantOccupancyAgeThresholds'

export type AssistantRoomOccupancy = Pick<
  App.Occupants,
  'adults' | 'children' | 'teenagers' | 'infants' | 'childrenAge'
>

export interface FormatAssistantRoomOccupancyTextOptions {
  ageThresholds?: AssistantOccupancyAgeThresholds
}

type AssistantOccupancyCounts = Pick<Required<App.Occupants>, App.OccupantType>

export interface PreparedAssistantRoomOccupancy {
  storage: AssistantRoomOccupancy
  resolved: App.Occupants
  counts: AssistantOccupancyCounts
  childAges: Array<number>
}

function hasNonZeroExplicitGuestCounts(room: AssistantRoomOccupancy): boolean {
  return (room.children ?? 0) > 0 ||
    (room.teenagers ?? 0) > 0 ||
    (room.infants ?? 0) > 0
}

function storageForAssistantSnapshot(
  room: AssistantRoomOccupancy,
  validAges: Array<number>,
): AssistantRoomOccupancy {
  if (!validAges.length) {
    return onlyDefinedProperties({
      adults: room.adults,
      children: room.children,
      teenagers: room.teenagers,
      infants: room.infants,
    })
  }

  if (hasNonZeroExplicitGuestCounts(room)) {
    return onlyDefinedProperties({
      adults: room.adults,
      children: room.children,
      teenagers: room.teenagers,
      infants: room.infants,
      childrenAge: validAges,
    })
  }

  return {
    adults: room.adults,
    childrenAge: validAges,
  }
}

function resolveAssistantRoomOccupancy(
  room: AssistantRoomOccupancy,
  ageThresholds?: AssistantOccupancyAgeThresholds,
): App.Occupants {
  const validAges = sanitiseChildrenAges(room.childrenAge ?? [])
  const searchThresholds = toSearchStringAgeThresholds(ageThresholds)

  if (validAges.length > 0 && !hasNonZeroExplicitGuestCounts(room)) {
    return getOccupancyFromSearchStrings(
      room.adults,
      validAges,
      searchThresholds.maxChildAge,
      searchThresholds.maxInfantAge,
      searchThresholds.maxTeenagerAge,
    )
  }

  if (validAges.length > 0) {
    return {
      adults: room.adults,
      children: room.children ?? 0,
      teenagers: room.teenagers ?? 0,
      infants: room.infants ?? 0,
      childrenAge: validAges,
    }
  }

  return onlyDefinedProperties({
    adults: room.adults,
    children: room.children,
    teenagers: room.teenagers,
    infants: room.infants,
  })
}

function prepareAssistantRoomOccupancy(
  room: AssistantRoomOccupancy,
  options?: FormatAssistantRoomOccupancyTextOptions,
): PreparedAssistantRoomOccupancy {
  const validAges = sanitiseChildrenAges(room.childrenAge ?? [])
  const resolved = resolveAssistantRoomOccupancy(room, options?.ageThresholds)

  return {
    storage: storageForAssistantSnapshot(room, validAges),
    resolved,
    counts: countOccupants([resolved]),
    childAges: sanitiseChildrenAges(resolved.childrenAge ?? []),
  }
}

export function normalizeAssistantRoomOccupancy(room: AssistantRoomOccupancy): AssistantRoomOccupancy {
  return prepareAssistantRoomOccupancy(room).storage
}

/** Age suffix is omitted when stored counts and ages disagree (inconsistent snapshot data). */
function childAgesMatchGuestCounts(
  childAges: Array<number>,
  counts: Pick<Required<App.Occupants>, 'children' | 'infants' | 'teenagers'>,
): boolean {
  if (!childAges.length) {
    return false
  }

  const nonAdultCount = counts.children + counts.infants + counts.teenagers
  return childAges.length === nonAdultCount
}

// This text is model-facing (part of the hidden enrichment context sent to the
// assistant), not user-facing — deliberately hardcoded English rather than
// i18n'd, matching the sibling buildOfferContext context-builder in the same
// enrichment pipeline. Translating the model's own context doesn't help it
// (the rest of that context stays English regardless), and it would risk a
// mixed-language context block once these strings were actually translated.
function formatAssistantChildAgesSuffix(childAges: Array<number>): string {
  return `(${getPlural('age', childAges.length)} ${childAges.join(', ')})`
}

export function formatAssistantRoomLabel(roomNumber: number, guests: string): string {
  return `Room ${roomNumber}: ${guests}`
}

const ASSISTANT_MULTI_ROOM_LINE_SEPARATOR = '; '

/** Semicolons keep room boundaries unambiguous after snapshot whitespace sanitization. */
function formatAssistantRoomLinesForModelContext(roomLines: Array<string>): string {
  return roomLines.join(ASSISTANT_MULTI_ROOM_LINE_SEPARATOR)
}

function formatPreparedAssistantRoomOccupancyText(
  prepared: PreparedAssistantRoomOccupancy,
): string | undefined {
  const base = formatOccupantsShort(prepared.resolved)
  if (!base) {
    return undefined
  }

  const { children, infants, teenagers } = prepared.counts
  if (!childAgesMatchGuestCounts(prepared.childAges, { children, infants, teenagers })) {
    return base
  }

  return `${base} ${formatAssistantChildAgesSuffix(prepared.childAges)}`
}

export function formatAssistantRoomOccupancyText(
  room: AssistantRoomOccupancy,
  options?: FormatAssistantRoomOccupancyTextOptions,
): string | undefined {
  return formatPreparedAssistantRoomOccupancyText(prepareAssistantRoomOccupancy(room, options))
}

export function formatAssistantRoomOccupancyLines(
  rooms: Array<AssistantRoomOccupancy>,
  options?: FormatAssistantRoomOccupancyTextOptions,
): Array<string> {
  const preparedRooms = prepareAssistantRoomOccupancies(rooms, options)

  return nonNullable(preparedRooms.map((prepared, index) => {
    const guests = formatPreparedAssistantRoomOccupancyText(prepared)
    return guests ? formatAssistantRoomLabel(index + 1, guests) : undefined
  }))
}

export function formatAssistantRoomsOccupancyText(
  rooms: Array<AssistantRoomOccupancy>,
  options?: FormatAssistantRoomOccupancyTextOptions,
): string | undefined {
  const roomLines = formatAssistantRoomOccupancyLines(rooms, options)
  if (!roomLines.length) {
    return undefined
  }

  return formatAssistantRoomLinesForModelContext(roomLines)
}

export function sumPreparedAssistantRoomOccupancyTotals(
  preparedRooms: Array<PreparedAssistantRoomOccupancy>,
): {
  totalAdults: number
  totalChildren: number
  totalInfants: number
} {
  return preparedRooms.reduce((totals, { counts }) => ({
    totalAdults: totals.totalAdults + counts.adults,
    totalChildren: totals.totalChildren + counts.children + counts.teenagers,
    totalInfants: totals.totalInfants + counts.infants,
  }), {
    totalAdults: 0,
    totalChildren: 0,
    totalInfants: 0,
  })
}

export function prepareAssistantRoomOccupancies(
  rooms: Array<AssistantRoomOccupancy>,
  options?: FormatAssistantRoomOccupancyTextOptions,
): Array<PreparedAssistantRoomOccupancy> {
  return rooms.map(room => prepareAssistantRoomOccupancy(room, options))
}
